import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useSubscription } from '../../contexts/SubscriptionContext';
import { useUpgradeModal } from '../../contexts/UpgradeModalContext';
import { BillingDA } from '../../services/admin/billing.service';
import { authCompany } from '../../scripts/authentication.script';
import { StateLanguage } from '../../languages/config/StateLanguage';
import { dateFormat } from '../../scripts/date.time.format.script';
import { BillingInvoice } from '../../models/billing.model';

const SubscriptionBanner: React.FC = () => {
  const { snapshot, loading, can: canSub } = useSubscription();
  const { openUpgradeModal } = useUpgradeModal();
  const navigate = useNavigate();
  const { lang } = StateLanguage();
  const [payableInvoices, setPayableInvoices] = useState<BillingInvoice[]>([]);

  const status = snapshot?.subscription?.status;
  const planChosen = snapshot?.subscription?.plan_chosen === true;

  // Fetch payable invoices when trial + plan chosen — same source as Subscription.tsx
  useEffect(() => {
    if (loading || !snapshot) return;
    if (status !== 'trial' || !planChosen) return;
    const company = authCompany();
    if (!company?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await BillingDA.getInvoices(company.id, 1, 100);
        if (cancelled) return;
        if (response.status === 200 && response.data) {
          const items: BillingInvoice[] = response.data.items ?? response.data.invoices ?? (Array.isArray(response.data) ? response.data : []);
          setPayableInvoices(items.filter(inv => inv.payable));
        }
      } catch {
        // Silently ignore
      }
    })();
    return () => { cancelled = true; };
  }, [loading, snapshot, status, planChosen]);

  if (loading || !snapshot) return null;
  if (!status || status === 'active') return null;

  // Only show action button if user can manage billing
  const canManageBilling = canSub('manage', 'digital:auth:entity:subscription:*').allowed;

  const formatDate = (iso?: string): string => {
    if (!iso) return '';
    return dateFormat(lang.code, iso);
  };

  const daysUntil = (iso?: string): number => {
    if (!iso) return 0;
    const target = new Date(iso).getTime();
    const now = Date.now();
    return Math.max(0, Math.ceil((target - now) / (1000 * 60 * 60 * 24)));
  };

  const interpolate = (template: string, vars: Record<string, string | number>): string => {
    let result = template;
    for (const [key, value] of Object.entries(vars)) {
      result = result.replace(`{${key}}`, String(value));
    }
    return result;
  };

  let message = '';
  let bgColor = '';
  let textColor = '#fff';
  let actionLabel = '';
  let actionHandler: (() => void) | null = null;

  switch (status) {
    case 'trial': {
      // Chosen and nothing due → no banner at all
      if (planChosen && payableInvoices.length === 0) return null;

      const date = formatDate(snapshot.subscription?.trial_end);
      const days = daysUntil(snapshot.subscription?.trial_end);
      message = interpolate(lang.labels.subscriptionTrialBanner, { date, days });
      bgColor = '#3699FF';
      if (canManageBilling) {
        if (!planChosen) {
          actionLabel = lang.labels.subscriptionModalTitleChoose;
          actionHandler = () => openUpgradeModal();
        } else {
          // planChosen && payableInvoices.length > 0 (guaranteed by guard above)
          actionLabel = lang.labels.subscriptionPayMyInvoice;
          actionHandler = () => navigate('/main/admin/subscription');
        }
      }
      break;
    }
    case 'past_due': {
      const date = formatDate(snapshot.subscription?.current_period_end);
      message = interpolate(lang.labels.subscriptionPastDueBanner, { date });
      bgColor = '#FFA800';
      actionLabel = canManageBilling ? lang.labels.subscriptionPayNow : '';
      actionHandler = () => openUpgradeModal();
      break;
    }
    case 'expired': {
      message = lang.labels.subscriptionExpiredBanner;
      bgColor = '#F64E60';
      actionLabel = canManageBilling ? lang.labels.subscriptionPayNow : '';
      actionHandler = () => openUpgradeModal();
      break;
    }
    case 'cancelled': {
      if (!snapshot.subscription?.cancel_at_period_end) return null;
      const date = formatDate(snapshot.subscription?.current_period_end);
      message = interpolate(lang.labels.subscriptionCancelledBanner, { date });
      bgColor = '#8950FC';
      actionLabel = canManageBilling ? lang.labels.subscriptionModalTitleChange : '';
      actionHandler = () => openUpgradeModal();
      break;
    }
    default:
      return null;
  }

  return (
    <div style={{
      backgroundColor: bgColor,
      color: textColor,
      padding: '8px 16px',
      textAlign: 'center',
      fontSize: '13px',
      fontWeight: 500,
      lineHeight: '1.5',
      zIndex: 100,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '12px',
      flexWrap: 'wrap',
    }}>
      <span>{message}</span>
      {actionLabel && actionHandler && (
        <button
          type="button"
          onClick={actionHandler}
          style={{
            backgroundColor: 'rgba(255,255,255,0.2)',
            color: textColor,
            border: '1px solid rgba(255,255,255,0.4)',
            borderRadius: '4px',
            padding: '2px 12px',
            fontSize: '12px',
            fontWeight: 600,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          {actionLabel}
        </button>
      )}
    </div>
  );
};

export default SubscriptionBanner;
